import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image';

import { animated, config, useTransition } from 'react-spring';

import HeaderBox from '../header-box';
import './styles.css';

import {
    // AeroPayIcon,
    APOPMediaIcon,
    DutchieIcon,
    IHeartJaneIcon,
    LeaflyIcon,
    TokrIcon,
    VectorPaymentsIcon,
} from '../icons';

const IntegrationsGrid = ({ copy, header, interval }) => {
    const data = useStaticQuery(graphql`
        query {
            metrc: file(relativePath: { eq: "logo-metrc.png" }) {
                childImageSharp {
                    fluid(maxWidth: 400) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            springBig: file(relativePath: { eq: "logo-springBig.jpg" }) {
                childImageSharp {
                    fluid(maxWidth: 400) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            aeropay: file(relativePath: { eq: "logo-aeropay.png" }) {
                childImageSharp {
                    fluid(maxWidth: 400) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
        }
    `);

    let displayCount = 2;

    if (typeof window !== `undefined`) {
        const width = window.innerWidth;
        if (width > 600) {
            displayCount = 3;
        }
        if (width > 900) {
            displayCount = 4;
        }
    }

    const integrationsList = [
        {
            name: 'Metrc',
            active: false,
            containerStyles: {
                backgroundColor: 'rgb(47, 46, 46)',
                borderRadius: '6px',
                marginBottom: '2px',
                padding: '0.5rem',
                width: '125px',
            },
            icon: <Img fluid={data.metrc.childImageSharp.fluid} />,
        },
        {
            name: 'Vector Payments',
            active: false,
            icon: <VectorPaymentsIcon />,
        },
        {
            name: 'APOP Media',
            active: false,
            icon: <APOPMediaIcon />,
        },
        {
            name: 'Leafly',
            active: false,
            containerStyles: {
                marginTop: '6px',
            },
            icon: <LeaflyIcon />,
        },
        {
            name: 'Dutchie',
            active: false,
            containerStyles: {
                width: '120px',
            },
            icon: <DutchieIcon />,
        },
        {
            name: 'AeroPay',
            active: false,
            containerStyles: {
                width: '162px',
            },
            icon: <Img fluid={data.aeropay.childImageSharp.fluid} />,
        },
        // {
        //     name: "Spring Big",
        //     active: false,
        //     containerStyles: {
        //         width: "175px",
        //     },
        //     icon: <Img fluid={data.springBig.childImageSharp.fluid} />,
        // },
        {
            name: 'Tokr',
            active: false,
            containerStyles: {
                width: '75px',
            },
            icon: <TokrIcon />,
        },
        {
            name: 'iHeartJane',
            active: false,
            icon: <IHeartJaneIcon />,
        },
    ];

    const firstLoad = useRef(true);
    // Array to hold timers from `setTimeout`
    const timers = useRef([]);

    useEffect(() => {
        // Clear the timers for cleanup
        return function cleanup() {
            // eslint-disable-next-line
            for (let timer of timers.current) {
                clearTimeout(timer);
            }
        };
    }, []);

    /**
     * Scramble the initial timeouts so the swap order is random.
     * Create an array of indexes, then shuffle it,
     * then set initial timeouts by shuffled index order.
     */
    const startSwapping = () => {
        // Create array of indices based on `displayCount`
        const indices = [];
        for (let i = 0; i < displayCount; i++) {
            indices.push(i);
        }

        // Variable for shuffling
        var currentIndex = indices.length,
            temporaryValue,
            randomIndex;

        // While there remain elements to shuffle...
        while (0 !== currentIndex) {
            // Pick a remaining element...
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex -= 1;

            // And swap it with the current element.
            temporaryValue = indices[currentIndex];
            indices[currentIndex] = indices[randomIndex];
            indices[randomIndex] = temporaryValue;
        }

        // Set the timeouts
        for (let i = 0; i < displayCount; i++) {
            const timer = setTimeout(
                () => updateCurrentItem(indices[i]),
                interval * (i + 1)
            );
            // Save the timer so it can be cleared
            timers.current[i] = timer;
        }
    };

    // Holds the items and their `active` state.
    // Also sets initial `setTimeout`s to kick off swapping.
    const [currentItems, setCurrentItems] = useState(() => {
        const items = integrationsList;
        const returnArray = items.map((i, num) => {
            if (num < displayCount) {
                i.active = true;
                // Add placeholder to `timers` array
                timers.current.push(num);
            }
            return i;
        });
        startSwapping();
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', async function () {
                // If the visibility changes to visible, lets re-start
                if (document.visibilityState === 'visible') {
                    console.log('Clearing and starting over');
                    await clearTimeouts();
                    startSwapping();
                }
            });
        }

        return returnArray;
    });

    const clearTimeouts = async () => {
        for (let timer of timers.current) {
            clearTimeout(timer);
        }
        return true;
    };

    // animations
    const transitions = useTransition(
        currentItems.filter(i => i.active),
        item => item.name, // item keys
        {
            config: config.smooth,
            unique: true, // items going in and out with the same key will be re-used

            // `from` sets the base values
            from: { opacity: 0, columnAdjust: 0 },
            // when item(s) enter, styles move from `from` to `enter`
            enter: { opacity: 1, columnAdjust: 0 },
            // when item(s) leave, styles move from `enter` to `leave`
            leave: { opacity: 0, columnAdjust: -1 },
        }
    );

    /**
     * Sets `active` property of the item at `index` to `false`
     * Activates an inactive item, then creates timeout to
     * replace the new item to keep the cycle going.
     * @param {number} index
     */
    const updateCurrentItem = index => {
        setCurrentItems(prevState => {
            const array = [...prevState];

            let filtered = array.filter(i => !i.active);
            let newItem = {};
            if (filtered.length) {
                newItem = filtered[0];
                newItem.active = true;
            }

            let returnArray = [];
            let old = {};

            for (let i = 0; i < array.length; i++) {
                // If we're at the index we need to update
                if (i === index) {
                    // Save the current item at the index so it can be moved to end of array
                    old = array[i];
                    old.active = false;
                    // Add `newItem` in array at the index we are updating
                    returnArray.push(newItem);
                } else if (newItem.name !== array[i].name) {
                    returnArray.push(array[i]);
                }
            }
            returnArray.push(old);

            return returnArray;
        });

        // Set timeout to replace this item.
        // Use `interval * displayCount` to keep swapping staggered
        const timer = setTimeout(
            () => updateCurrentItem(index),
            interval * displayCount
        );
        // Save the timer so it can be cleared
        timers.current[index] = timer;
    };

    /**
     * OFFSET/ADJUSTMENTS BREAKDOWN
     * When a swap is animiating, there is an additional item being displayed
     * because the old item is leaving while the new item is entering.
     * That means we must manually position the elements in the grid, so the
     * overlapping items actually overlap, rather than make the grid bigger.
     * The `grid-column` property on each `animated.div` is the key here.
     *
     * To begin, each item should be in the `index + 1` column. Simple enough.
     *
     * Next, we offset any item that is `leaving` because it should sit within the
     * same grid column as the item that is `entering`. react-spring sets those states,
     * and we use `columnAdjust` within the `useTransition` hook to offset `leaving` by 1.
     *
     * Finally, after a swap has occured in the map, we need to offset the remaining
     * items by 1 because the `index` will be one greater than it should after the offset,
     * and that is managed by `hasSwappedAdjustment`. Note that `hasSwappedAdjustment`
     * won't take effect for the `firstLoad` because ALL items are `entering` at that point.
     */
    let hasSwappedAdjustment = 0; // needed to remember if an adjustment is needed
    return (
        <section className="integration-container integrations-header">
            <HeaderBox copy={copy} header={header} />
            <div className="integrations-grid">
                {transitions.map(
                    ({ item, key, props: styles, state }, index) => {
                        if (state === 'enter' && !firstLoad.current) {
                            hasSwappedAdjustment = 1;
                        }
                        if (
                            firstLoad.current === true &&
                            index === displayCount - 1
                        ) {
                            firstLoad.current = false;
                        }

                        return (
                            <animated.div
                                className="integration"
                                key={key}
                                style={{
                                    ...styles,
                                    ...item.containerStyles,
                                    gridColumn:
                                        index +
                                        1 +
                                        styles.columnAdjust.startPosition -
                                        hasSwappedAdjustment,
                                    gridRow: 1, // forces overlap in the same grid cell
                                }}
                            >
                                {item && item.icon}
                            </animated.div>
                        );
                    }
                )}
            </div>
        </section>
    );
};

IntegrationsGrid.defaultProps = {
    displayCount: 2,
    interval: 1500,
};

IntegrationsGrid.propTypes = {
    copy: PropTypes.string,
    displayCount: PropTypes.number,
    header: PropTypes.string,
    interval: PropTypes.number,
};

export default IntegrationsGrid;
